import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, unwrap } from "@/lib/api";
import { GlossLogomark } from "@/components/GlossLogomark";
import { useSettings } from "@/contexts/SettingsContext";
import { Check, Loader2, AlertCircle } from "lucide-react";

/**
 * Public, mobile-first self-check-in page.
 * Reached via QR code on the Waiting Display: /checkin?token=S-003
 *
 * No auth required. State machine moves booked → checked-in idempotently.
 */
export default function CheckIn() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const branch_id = params.get("branch_id") || "branch-001";
  const { settings } = useSettings();
  const [state, setState] = useState("idle"); // idle | submitting | done | error
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  // Light pre-fetch so the UI can show the masked name before the user taps
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!token) return;
    api.get("/appointments/public", { params: { branch_id } })
      .then((r) => {
        const list = unwrap(r) || [];
        const found = list.find((a) => a.token_number === token);
        if (found) setPreview(found);
      })
      .catch(() => {});
  }, [token, branch_id]);

  const submit = async () => {
    if (!token) return;
    setState("submitting");
    try {
      const r = await api.post("/appointments/checkin", { token, branch_id });
      setResult(unwrap(r));
      setState("done");
    } catch (e) {
      const data = e?.response?.data?.error;
      setErrorMsg(data?.message || "Something went wrong. Please show this screen to reception.");
      setState("error");
    }
  };

  if (!token) {
    return (
      <Shell salonName={settings.salon_name}>
        <ErrorBlock msg="No token in this link. Please scan the QR on the display again." />
      </Shell>
    );
  }

  return (
    <Shell salonName={settings.salon_name}>
      {state === "done" && result ? (
        <div className="space-y-5 text-center" data-testid="checkin-success">
          <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center" style={{ background: "rgba(22,163,74,0.14)", border: "1px solid rgba(22,163,74,0.30)" }}>
            <Check size={42} className="text-[var(--color-success-deep)]" strokeWidth={3} />
          </div>
          <div>
            <h1 className="font-display text-[32px] font-bold tracking-[-0.02em]">You've been checked in</h1>
            <p className="font-inter text-[14.5px] text-[var(--color-text-muted)] mt-2">
              Please take a seat — your stylist will call you shortly.
            </p>
          </div>
          <div className="rounded-2xl p-5 inline-block" style={{ background: "rgba(212,168,83,0.10)", border: "1px solid rgba(212,168,83,0.30)" }}>
            <div className="overline mb-1">Your token</div>
            <div className="font-mono text-[44px] leading-none text-[var(--color-gold-deeper)]">{result.token_number}</div>
            <div className="font-inter text-[13px] text-[var(--color-text-secondary)] mt-2">{result.masked_name}</div>
            {result.already_checked_in && (
              <div className="font-inter text-[11.5px] text-[var(--color-text-muted)] mt-2 italic">
                You were already checked in — all set.
              </div>
            )}
          </div>
        </div>
      ) : state === "error" ? (
        <ErrorBlock msg={errorMsg} />
      ) : (
        <div className="space-y-5">
          <div>
            <div className="overline" style={{ color: "#5E4060" }}>Self check-in</div>
            <h1 className="font-display text-[28px] font-bold tracking-[-0.02em] mt-1">
              Hi {preview?.masked_name || "there"}!
            </h1>
            <p className="font-inter text-[14.5px] text-[var(--color-text-muted)] mt-2">
              Your stylist is almost ready. Tap below to confirm you're here.
            </p>
          </div>

          <div className="rounded-2xl p-4 flex items-center justify-between" style={{ background: "rgba(94,64,96,0.06)", border: "1px solid rgba(94,64,96,0.20)" }}>
            <div>
              <div className="overline" style={{ color: "#5E4060" }}>Your token</div>
              <div className="font-mono text-[36px] leading-none text-[var(--color-mauve-deep)] mt-1">{token}</div>
            </div>
            {preview?.services_requested?.length > 0 && (
              <div className="text-right">
                <div className="overline">Service</div>
                <div className="font-sora font-semibold text-[14px] text-[var(--color-text-primary)] mt-1">
                  {preview.services_requested.join(" + ")}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={submit}
            disabled={state === "submitting"}
            data-testid="checkin-confirm-btn"
            className="btn-3d btn-gold-3d btn-lg w-full"
          >
            {state === "submitting" ? (
              <>
                <Loader2 size={18} className="animate-spin" /> Checking you in…
              </>
            ) : (
              <>
                <Check size={18} /> I'm Here
              </>
            )}
          </button>

          <p className="text-center font-inter text-[11.5px] text-[var(--color-text-muted)]">
            Need help? Show this screen to reception.
          </p>
        </div>
      )}
    </Shell>
  );
}

function ErrorBlock({ msg }) {
  return (
    <div className="space-y-4 text-center" data-testid="checkin-error">
      <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center" style={{ background: "rgba(185,28,28,0.10)", border: "1px solid rgba(185,28,28,0.28)" }}>
        <AlertCircle size={32} className="text-[var(--color-danger-deep)]" />
      </div>
      <p className="font-inter text-[14.5px] text-[var(--color-text-secondary)]">{msg}</p>
    </div>
  );
}

function Shell({ salonName, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-5" style={{
      background: "radial-gradient(ellipse 50% 60% at 50% 0%, rgba(212,168,83,0.18) 0%, transparent 60%), linear-gradient(180deg, #FDF9F4 0%, #F7EFE6 100%)",
    }}>
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <GlossLogomark size={40} animate={false} />
          <div className="font-display text-[15px] font-semibold mt-2 text-[var(--color-text-primary)]">{salonName}</div>
        </div>
        <div className="glass-elevated rounded-[28px] p-7 anim-slide-up-lg">
          {children}
        </div>
      </div>
    </div>
  );
}
